import React, { useState, useEffect } from 'react';
import { 
  BookOpen, 
  Star, 
  Users, 
  Eye, 
  Heart, 
  Share2, 
  Download, 
  Filter, 
  Search, 
  Grid, 
  List, 
  Clock, 
  Bookmark, 
  MessageCircle,
  TrendingUp,
  Award,
  ChevronLeft,
  ChevronRight,
  Play,
  Pause,
  Volume2,
  Settings,
  X,
  Plus
} from 'lucide-react';

const ELibrary = () => {
  const [darkMode, setDarkMode] = useState(true);
  const [viewMode, setViewMode] = useState('grid'); // grid or list
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedBook, setSelectedBook] = useState(null);
  const [isReading, setIsReading] = useState(false);
  const [readingProgress, setReadingProgress] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [bookmarkPage, setBookmarkPage] = useState(null);
  const [isAudioMode, setIsAudioMode] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1.0);

  // Enhanced book data with more sophisticated properties
  const eLibrary = [
    {
      id: 1,
      title: "The Psychology of Learning",
      author: "Dr. Sarah Mitchell",
      category: "Psychology",
      cover: "https://images.unsplash.com/photo-1481627834876-b7833e8f5570?w=300&h=400&fit=crop",
      rating: 4.8,
      totalRatings: 2847,
      totalReaders: 12459,
      pages: 324,
      duration: "8h 42m",
      language: "English",
      publishYear: 2023,
      isbn: "978-0-123456-78-9",
      genre: ["Psychology", "Education", "Self-Help"],
      description: "A comprehensive guide to understanding how the human mind processes and retains information, with practical applications for students and educators.",
      progress: 67,
      lastRead: "2 days ago",
      bookmarked: true,
      downloaded: true,
      highlights: 23,
      notes: 12,
      preview: "Learning is not a passive absorption of information but an active process of construction...",
      reviews: [
        { author: "John D.", rating: 5, text: "Incredibly insightful and practical. Changed how I approach learning." },
        { author: "Maria S.", rating: 4, text: "Well-researched with excellent examples. Highly recommended." }
      ],
      chapters: [
        { title: "Introduction to Learning Theory", pages: 15, completed: true },
        { title: "Cognitive Processing Models", pages: 28, completed: true },
        { title: "Memory and Retention", pages: 32, completed: false },
        { title: "Practical Applications", pages: 25, completed: false }
      ]
    },
    {
      id: 2,
      title: "Digital Transformation",
      author: "Alex Chen",
      category: "Technology",
      cover: "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=300&h=400&fit=crop",
      rating: 4.6,
      totalRatings: 1923,
      totalReaders: 8734,
      pages: 287,
      duration: "7h 15m",
      language: "English",
      publishYear: 2024,
      isbn: "978-0-987654-32-1",
      genre: ["Technology", "Business", "Innovation"],
      description: "Navigate the complex landscape of digital transformation with proven strategies and real-world case studies.",
      progress: 34,
      lastRead: "1 week ago",
      bookmarked: false,
      downloaded: false,
      highlights: 8,
      notes: 5,
      preview: "In today's rapidly evolving digital landscape, organizations must adapt or risk obsolescence...",
      reviews: [
        { author: "Tech Leader", rating: 5, text: "Essential reading for any business leader in the digital age." },
        { author: "StartupCEO", rating: 4, text: "Practical insights with actionable frameworks." }
      ],
      chapters: [
        { title: "The Digital Imperative", pages: 22, completed: true },
        { title: "Technology Stack Evolution", pages: 35, completed: false },
        { title: "Change Management", pages: 40, completed: false }
      ]
    },
    {
      id: 3,
      title: "Mindful Leadership",
      author: "Dr. Emma Thompson",
      category: "Leadership",
      cover: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=300&h=400&fit=crop",
      rating: 4.9,
      totalRatings: 3456,
      totalReaders: 15623,
      pages: 298,
      duration: "9h 22m",
      language: "English",
      publishYear: 2023,
      isbn: "978-0-456789-12-3",
      genre: ["Leadership", "Mindfulness", "Management"],
      description: "Discover how mindfulness practices can transform your leadership style and create more effective, compassionate teams.",
      progress: 89,
      lastRead: "Yesterday",
      bookmarked: true,
      downloaded: true,
      highlights: 45,
      notes: 28,
      preview: "True leadership begins with leading oneself. Mindful leadership is about bringing full attention...",
      reviews: [
        { author: "Executive Coach", rating: 5, text: "Transformative approach to leadership. Highly practical." },
        { author: "Team Manager", rating: 5, text: "Changed my entire management philosophy." }
      ],
      chapters: [
        { title: "Foundations of Mindful Leadership", pages: 25, completed: true },
        { title: "Self-Awareness in Leadership", pages: 30, completed: true },
        { title: "Building Mindful Teams", pages: 35, completed: true },
        { title: "Sustainable Leadership Practices", pages: 28, completed: false }
      ]
    },
    {
      id: 4,
      title: "The Future of Work",
      author: "Prof. Michael Rodriguez",
      category: "Business",
      cover: "https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=300&h=400&fit=crop",
      rating: 4.4,
      totalRatings: 1567,
      totalReaders: 6789,
      pages: 356,
      duration: "10h 18m",
      language: "English",
      publishYear: 2024,
      isbn: "978-0-234567-89-0",
      genre: ["Business", "Future Studies", "HR"],
      description: "An exploration of how work is evolving in the digital age and what it means for organizations and individuals.",
      progress: 12,
      lastRead: "3 weeks ago",
      bookmarked: false,
      downloaded: true,
      highlights: 3,
      notes: 1,
      preview: "The workplace of tomorrow is being shaped today by technological advances, changing demographics...",
      reviews: [
        { author: "HR Director", rating: 4, text: "Insightful predictions with solid research backing." },
        { author: "Business Analyst", rating: 4, text: "Great framework for understanding workplace evolution." }
      ],
      chapters: [
        { title: "The Changing Landscape", pages: 42, completed: true },
        { title: "Remote Work Revolution", pages: 38, completed: false },
        { title: "AI and Human Collaboration", pages: 45, completed: false }
      ]
    }
  ];

  const categories = ['all', 'Psychology', 'Technology', 'Leadership', 'Business'];

  const filteredBooks = eLibrary.filter(book => {
    const matchesCategory = selectedCategory === 'all' || book.category === selectedCategory;
    const matchesSearch = book.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         book.author.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const openBookDetails = (book) => {
    setSelectedBook(book);
  };

  const closeBookDetails = () => {
    setSelectedBook(null);
    setIsReading(false);
  };

  const startReading = () => {
    setIsReading(true);
    setCurrentPage(Math.floor((selectedBook.progress / 100) * selectedBook.pages) || 1);
  };

  const toggleBookmark = () => {
    setBookmarkPage(currentPage);
  };

  const formatNumber = (num) => {
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'k';
    }
    return num.toString();
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        className={`w-4 h-4 ${i < Math.floor(rating) ? 'text-yellow-400 fill-current' : 'text-gray-300'}`}
      />
    ));
  };

  const BookCard = ({ book }) => (
    <div 
      className={`group relative overflow-hidden rounded-2xl ${darkMode ? 'bg-gray-800 hover:bg-gray-700' : 'bg-white hover:bg-gray-50'} shadow-lg hover:shadow-2xl transition-all duration-500 cursor-pointer`}
      onClick={() => openBookDetails(book)}
    >
      {/* Book Cover */}
      <div className="relative overflow-hidden">
        <img 
          src={book.cover} 
          alt={book.title}
          className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-500"
        />
        
        {/* Overlay with quick actions */}
        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center space-x-3">
          <button className="p-2 bg-white/20 backdrop-blur-sm rounded-full hover:bg-white/30 transition-colors">
            <Eye className="w-5 h-5 text-white" />
          </button>
          <button className="p-2 bg-white/20 backdrop-blur-sm rounded-full hover:bg-white/30 transition-colors">
            <BookOpen className="w-5 h-5 text-white" />
          </button>
          <button className="p-2 bg-white/20 backdrop-blur-sm rounded-full hover:bg-white/30 transition-colors">
            <Heart className="w-5 h-5 text-white" />
          </button>
        </div>

        {/* Progress indicator */}
        {book.progress > 0 && (
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-black/20">
            <div 
              className="h-full bg-blue-500 transition-all duration-300"
              style={{ width: `${book.progress}%` }}
            />
          </div>
        )}

        {/* Badges */}
        <div className="absolute top-3 left-3 flex flex-col space-y-2">
          {book.bookmarked && (
            <div className="px-2 py-1 bg-blue-500 text-white text-xs rounded-full">
              Bookmarked
            </div>
          )}
          {book.downloaded && (
            <div className="px-2 py-1 bg-green-500 text-white text-xs rounded-full">
              Downloaded
            </div>
          )}
        </div>

        {/* Rating badge */}
        <div className="absolute top-3 right-3 px-2 py-1 bg-black/70 backdrop-blur-sm text-white text-xs rounded-full flex items-center">
          <Star className="w-3 h-3 text-yellow-400 fill-current mr-1" />
          {book.rating}
        </div>
      </div>

      {/* Book Details */}
      <div className="p-4">
        <h3 className={`font-semibold text-lg mb-1 line-clamp-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
          {book.title}
        </h3>
        <p className={`text-sm mb-3 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          by {book.author}
        </p>

        {/* Stats */}
        <div className="flex items-center justify-between text-xs mb-3">
          <div className="flex items-center space-x-3">
            <span className={`flex items-center ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              <Users className="w-3 h-3 mr-1" />
              {formatNumber(book.totalReaders)}
            </span>
            <span className={`flex items-center ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              <MessageCircle className="w-3 h-3 mr-1" />
              {formatNumber(book.totalRatings)}
            </span>
          </div>
          <span className={`${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            {book.pages} pages
          </span>
        </div>

        {/* Progress bar */}
        {book.progress > 0 && (
          <div className="mb-3">
            <div className="flex justify-between text-xs mb-1">
              <span className={`${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Progress</span>
              <span className={`${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{book.progress}%</span>
            </div>
            <div className={`w-full h-2 rounded-full ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
              <div 
                className="h-2 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full transition-all duration-300"
                style={{ width: `${book.progress}%` }}
              />
            </div>
          </div>
        )}

        {/* Genre tags */}
        <div className="flex flex-wrap gap-1 mb-3">
          {book.genre.slice(0, 2).map((genre, index) => (
            <span 
              key={index}
              className={`px-2 py-1 text-xs rounded-full ${darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}
            >
              {genre}
            </span>
          ))}
        </div>

        {/* Action buttons */}
        <div className="flex space-x-2">
          <button 
            className="flex-1 py-2 px-3 bg-blue-500 hover:bg-blue-600 text-white text-sm rounded-lg transition-colors flex items-center justify-center"
            onClick={(e) => {
              e.stopPropagation();
              openBookDetails(book);
            }}
          >
            <BookOpen className="w-4 h-4 mr-1" />
            Read
          </button>
          <button 
            className={`p-2 rounded-lg transition-colors ${darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'}`}
            onClick={(e) => e.stopPropagation()}
          >
            <Download className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );

  const BookDetailModal = () => {
    if (!selectedBook) return null;

    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className={`max-w-4xl w-full max-h-[90vh] overflow-y-auto rounded-3xl ${darkMode ? 'bg-gray-900' : 'bg-white'} shadow-2xl`}>
          {!isReading ? (
            // Book Details View
            <div className="p-6">
              {/* Header */}
              <div className="flex justify-between items-start mb-6">
                <h2 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  Book Details
                </h2>
                <button 
                  onClick={closeBookDetails}
                  className={`p-2 rounded-full ${darkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-100'} transition-colors`}
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Book Cover and Actions */}
                <div className="lg:col-span-1">
                  <img 
                    src={selectedBook.cover}
                    alt={selectedBook.title}
                    className="w-full max-w-sm mx-auto rounded-2xl shadow-lg mb-6"
                  />
                  
                  {/* Action Buttons */}
                  <div className="space-y-3">
                    <button 
                      onClick={startReading}
                      className="w-full py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-xl font-medium transition-colors flex items-center justify-center"
                    >
                      <BookOpen className="w-5 h-5 mr-2" />
                      {selectedBook.progress > 0 ? 'Continue Reading' : 'Start Reading'}
                    </button>
                    
                    <div className="grid grid-cols-2 gap-3">
                      <button className={`py-2 px-4 rounded-xl font-medium transition-colors flex items-center justify-center ${darkMode ? 'bg-gray-800 hover:bg-gray-700' : 'bg-gray-200 hover:bg-gray-300'}`}>
                        <Download className="w-4 h-4 mr-1" />
                        Download
                      </button>
                      <button className={`py-2 px-4 rounded-xl font-medium transition-colors flex items-center justify-center ${darkMode ? 'bg-gray-800 hover:bg-gray-700' : 'bg-gray-200 hover:bg-gray-300'}`}>
                        <Heart className="w-4 h-4 mr-1" />
                        Favorite
                      </button>
                    </div>
                    
                    <button 
                      onClick={() => setIsAudioMode(!isAudioMode)}
                      className={`w-full py-2 px-4 rounded-xl font-medium transition-colors flex items-center justify-center ${isAudioMode ? 'bg-purple-500 hover:bg-purple-600 text-white' : (darkMode ? 'bg-gray-800 hover:bg-gray-700' : 'bg-gray-200 hover:bg-gray-300')}`}
                    >
                      <Volume2 className="w-4 h-4 mr-1" />
                      {isAudioMode ? 'Audio Mode ON' : 'Audio Mode'}
                    </button>
                  </div>
                </div>

                {/* Book Information */}
                <div className="lg:col-span-2 space-y-6">
                  {/* Title and Author */}
                  <div>
                    <h1 className={`text-3xl font-bold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      {selectedBook.title}
                    </h1>
                    <p className={`text-xl ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      by {selectedBook.author}
                    </p>
                  </div>

                  {/* Rating and Stats */}
                  <div className="flex items-center space-x-6">
                    <div className="flex items-center space-x-2">
                      <div className="flex">
                        {renderStars(selectedBook.rating)}
                      </div>
                      <span className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        {selectedBook.rating}
                      </span>
                      <span className={`${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        ({formatNumber(selectedBook.totalRatings)} reviews)
                      </span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Users className="w-4 h-4" />
                      <span>{formatNumber(selectedBook.totalReaders)} readers</span>
                    </div>
                  </div>

                  {/* Book Details */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className={`p-4 rounded-xl ${darkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
                      <div className="text-sm text-gray-500">Pages</div>
                      <div className="font-medium">{selectedBook.pages}</div>
                    </div>
                    <div className={`p-4 rounded-xl ${darkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
                      <div className="text-sm text-gray-500">Duration</div>
                      <div className="font-medium">{selectedBook.duration}</div>
                    </div>
                    <div className={`p-4 rounded-xl ${darkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
                      <div className="text-sm text-gray-500">Published</div>
                      <div className="font-medium">{selectedBook.publishYear}</div>
                    </div>
                    <div className={`p-4 rounded-xl ${darkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
                      <div className="text-sm text-gray-500">Language</div>
                      <div className="font-medium">{selectedBook.language}</div>
                    </div>
                  </div>

                  {/* Progress */}
                  {selectedBook.progress > 0 && (
                    <div className={`p-4 rounded-xl ${darkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
                      <div className="flex justify-between items-center mb-2">
                        <span className="font-medium">Reading Progress</span>
                        <span>{selectedBook.progress}%</span>
                      </div>
                      <div className={`w-full h-3 rounded-full ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                        <div 
                          className="h-3 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full"
                          style={{ width: `${selectedBook.progress}%` }}
                        />
                      </div>
                      <div className="text-sm text-gray-500 mt-1">
                        Last read: {selectedBook.lastRead}
                      </div>
                    </div>
                  )}

                  {/* Description */}
                  <div>
                    <h3 className={`text-lg font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      Description
                    </h3>
                    <p className={`${darkMode ? 'text-gray-300' : 'text-gray-700'} leading-relaxed`}>
                      {selectedBook.description}
                    </p>
                  </div>

                  {/* Genres */}
                  <div>
                    <h3 className={`text-lg font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      Genres
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {selectedBook.genre.map((genre, index) => (
                        <span 
                          key={index}
                          className={`px-3 py-1 rounded-full text-sm ${darkMode ? 'bg-gray-800 text-gray-300' : 'bg-gray-200 text-gray-700'}`}
                        >
                          {genre}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Chapters */}
                  <div>
                    <h3 className={`text-lg font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      Chapters
                    </h3>
                    <div className="space-y-2">
                      {selectedBook.chapters.map((chapter, index) => (
                        <div 
                          key={index}
                          className={`p-3 rounded-xl flex items-center justify-between ${darkMode ? 'bg-gray-800' : 'bg-gray-100'}`}
                        >
                          <div className="flex items-center space-x-3">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${chapter.completed ? 'bg-green-500 text-white' : (darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-600')}`}>
                              {index + 1}
                            </div>
                            <div>
                              <div className="font-medium">{chapter.title}</div>
                              <div className="text-sm text-gray-500">{chapter.pages} pages</div>
                            </div>
                          </div>
                          {chapter.completed && (
                            <div className="text-green-500">
                              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Reviews */}
                  <div>
                    <h3 className={`text-lg font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      Recent Reviews
                    </h3>
                    <div className="space-y-4">
                      {selectedBook.reviews.map((review, index) => (
                        <div 
                          key={index}
                          className={`p-4 rounded-xl ${darkMode ? 'bg-gray-800' : 'bg-gray-100'}`}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-medium">{review.author}</span>
                            <div className="flex">
                              {renderStars(review.rating)}
                            </div>
                          </div>
                          <p className={`${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            {review.text}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            // Reading View
            <div className="h-screen flex flex-col">
              {/* Reading Header */}
              <div className={`p-4 border-b ${darkMode ? 'border-gray-700 bg-gray-900' : 'border-gray-200 bg-white'} flex items-center justify-between`}>
                <div className="flex items-center space-x-4">
                  <button 
                    onClick={() => setIsReading(false)}
                    className={`p-2 rounded-full ${darkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-100'} transition-colors`}
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <div>
                    <h3 className="font-medium">{selectedBook.title}</h3>
                    <p className="text-sm text-gray-500">Page {currentPage} of {selectedBook.pages}</p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-2">
                  {isAudioMode && (
                    <div className="flex items-center space-x-2">
                      <button className="p-2 rounded-full bg-purple-500 text-white">
                        <Play className="w-4 h-4" />
                      </button>
                      <select 
                        value={playbackSpeed}
                        onChange={(e) => setPlaybackSpeed(parseFloat(e.target.value))}
                        className={`px-2 py-1 rounded text-sm ${darkMode ? 'bg-gray-800' : 'bg-gray-200'}`}
                      >
                        <option value={0.5}>0.5x</option>
                        <option value={1.0}>1.0x</option>
                        <option value={1.5}>1.5x</option>
                        <option value={2.0}>2.0x</option>
                      </select>
                    </div>
                  )}
                  
                  <button 
                    onClick={toggleBookmark}
                    className={`p-2 rounded-full transition-colors ${bookmarkPage === currentPage ? 'bg-blue-500 text-white' : (darkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-100')}`}
                  >
                    <Bookmark className="w-4 h-4" />
                  </button>
                  
                  <button className={`p-2 rounded-full ${darkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-100'} transition-colors`}>
                    <Settings className="w-4 h-4" />
                  </button>
                  
                  <button 
                    onClick={closeBookDetails}
                    className={`p-2 rounded-full ${darkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-100'} transition-colors`}
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Reading Content */}
              <div className="flex-1 p-8 overflow-y-auto">
                <div className="max-w-3xl mx-auto">
                  <div className={`prose prose-lg ${darkMode ? 'prose-invert' : ''} max-w-none`}>
                    <h2 className="text-2xl font-bold mb-6">
                      Chapter {Math.ceil(currentPage / 20)}: {selectedBook.chapters[Math.floor((currentPage - 1) / 20)]?.title}
                    </h2>
                    
                    <div className="space-y-6 leading-relaxed">
                      <p>
                        {selectedBook.preview} This is a simulated reading experience where you would see the actual book content. 
                        The text would be formatted beautifully with proper typography, spacing, and readability optimizations.
                      </p>
                      
                      <p>
                        In a real implementation, this would connect to your book database and display the actual page content. 
                        You could implement features like:
                      </p>
                      
                      <ul className="space-y-2">
                        <li>• Highlighting text passages</li>
                        <li>• Adding personal notes and annotations</li>
                        <li>• Adjusting font size and reading preferences</li>
                        <li>• Synchronizing reading progress across devices</li>
                        <li>• Voice narration with professional audio</li>
                      </ul>
                      
                      <p>
                        The reading experience would be fully customizable with options for font selection, 
                        background colors, line spacing, and various accessibility features to enhance readability.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Reading Controls */}
              <div className={`p-4 border-t ${darkMode ? 'border-gray-700 bg-gray-900' : 'border-gray-200 bg-white'}`}>
                <div className="flex items-center justify-between max-w-3xl mx-auto">
                  <button 
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${currentPage === 1 ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-100 dark:hover:bg-gray-800'}`}
                  >
                    <ChevronLeft className="w-4 h-4" />
                    <span>Previous</span>
                  </button>
                  
                  <div className="flex-1 mx-8">
                    <div className={`w-full h-2 rounded-full ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                      <div 
                        className="h-2 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full transition-all duration-300"
                        style={{ width: `${(currentPage / selectedBook.pages) * 100}%` }}
                      />
                    </div>
                    <div className="flex justify-between text-sm text-gray-500 mt-1">
                      <span>Page {currentPage}</span>
                      <span>{selectedBook.pages} pages</span>
                    </div>
                  </div>
                  
                  <button 
                    onClick={() => setCurrentPage(Math.min(selectedBook.pages, currentPage + 1))}
                    disabled={currentPage === selectedBook.pages}
                    className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${currentPage === selectedBook.pages ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-100 dark:hover:bg-gray-800'}`}
                  >
                    <span>Next</span>
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className={`min-h-screen transition-colors duration-300 ${darkMode ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'}`}>
      {/* Header */}
      <div className={`sticky top-0 z-40 ${darkMode ? 'bg-gray-900/95' : 'bg-white/95'} backdrop-blur-sm border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold">Digital Library</h1>
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Discover, read, and manage your digital collection
              </p>
            </div>
            
            <div className="flex items-center space-x-4">
              {/* Search */}
              <div className="relative">
                <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                <input
                  type="text"
                  placeholder="Search books, authors..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className={`pl-10 pr-4 py-2 w-64 rounded-xl border ${darkMode ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'} focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                />
              </div>
              
              {/* View Mode Toggle */}
              <div className={`flex items-center space-x-1 p-1 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-gray-200'}`}>
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded-lg transition-colors ${viewMode === 'grid' ? 'bg-blue-500 text-white' : (darkMode ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900')}`}
                >
                  <Grid className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded-lg transition-colors ${viewMode === 'list' ? 'bg-blue-500 text-white' : (darkMode ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900')}`}
                >
                  <List className="w-4 h-4" />
                </button>
              </div>
              
              {/* Dark Mode Toggle */}
              <button
                onClick={() => setDarkMode(!darkMode)}
                className={`p-2 rounded-lg transition-colors ${darkMode ? 'bg-gray-800 hover:bg-gray-700' : 'bg-gray-200 hover:bg-gray-300'}`}
              >
                {darkMode ? '🌙' : '☀️'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="max-w-7xl mx-auto px-6 py-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className={`p-4 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-blue-500 rounded-lg">
                <BookOpen className="w-5 h-5 text-white" />
              </div>
              <div>
                <div className="text-2xl font-bold">{eLibrary.length}</div>
                <div className="text-sm text-gray-500">Total Books</div>
              </div>
            </div>
          </div>
          
          <div className={`p-4 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-green-500 rounded-lg">
                <TrendingUp className="w-5 h-5 text-white" />
              </div>
              <div>
                <div className="text-2xl font-bold">{eLibrary.filter(b => b.progress > 0).length}</div>
                <div className="text-sm text-gray-500">In Progress</div>
              </div>
            </div>
          </div>
          
          <div className={`p-4 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-purple-500 rounded-lg">
                <Award className="w-5 h-5 text-white" />
              </div>
              <div>
                <div className="text-2xl font-bold">{eLibrary.filter(b => b.progress === 100).length}</div>
                <div className="text-sm text-gray-500">Completed</div>
              </div>
            </div>
          </div>
          
          <div className={`p-4 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-orange-500 rounded-lg">
                <Clock className="w-5 h-5 text-white" />
              </div>
              <div>
                <div className="text-2xl font-bold">
                  {Math.round(eLibrary.reduce((acc, book) => acc + parseFloat(book.duration.split('h')[0]), 0))}h
                </div>
                <div className="text-sm text-gray-500">Total Hours</div>
              </div>
            </div>
          </div>
        </div>

        {/* Category Filter */}
        <div className="flex items-center space-x-4 mb-8">
          <span className={`font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Categories:</span>
          <div className="flex flex-wrap gap-2">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  selectedCategory === category
                    ? 'bg-blue-500 text-white'
                    : (darkMode ? 'bg-gray-800 text-gray-300 hover:bg-gray-700' : 'bg-gray-200 text-gray-700 hover:bg-gray-300')
                }`}
              >
                {category === 'all' ? 'All Books' : category}
              </button>
            ))}
          </div>
        </div>

        {/* Books Grid */}
        <div className={`grid gap-6 ${viewMode === 'grid' ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4' : 'grid-cols-1'}`}>
          {filteredBooks.map((book) => (
            <BookCard key={book.id} book={book} />
          ))}
        </div>

        {/* Empty State */}
        {filteredBooks.length === 0 && (
          <div className="text-center py-12">
            <BookOpen className={`w-16 h-16 mx-auto mb-4 ${darkMode ? 'text-gray-600' : 'text-gray-400'}`} />
            <h3 className={`text-xl font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              No books found
            </h3>
            <p className={`${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Try adjusting your search or filter criteria
            </p>
          </div>
        )}
      </div>

      {/* Book Detail Modal */}
      <BookDetailModal />
    </div>
  );
};

export default ELibrary;